/**
 * 普通 DOM 界面的总编排组件。
 * 它不负责 3D 绘制，而是根据共享状态控制加载页、声音选择、章节文案、菜单和贺卡。
 */
import { createRef, RefObject, useRef } from 'react';
import { useActiveLetterState } from '../../atoms/active-letter';
import { usePreloaderReadyState } from '../../atoms/preloader-ready';
import { useScreensState } from '../../atoms/screens';
import { useSoundConfigState } from '../../atoms/sound-config';
import { data, lettersData } from '../../data';
import { useDebounce } from '../../hooks/use-debounce';
import Header from './Header';
import LetterPopup from './LetterPopup';
import Menu from './Menu';
import Message from './Message';
import Preloader from './Preloader';
import SoundConfiguratorScreen from './SoundConfiguratorScreen';
import ProgressRound from './ProgressRound';
import Sounds from './Sounds';
import classNames from 'classnames';

const Dom = () => {
    // 为每个章节文案保留稳定 ref，避免重新渲染时重复创建 DOM 引用。
    const messageRef = useRef<RefObject<HTMLDivElement>[]>([]);
    messageRef.current = Object.keys(data).map(
        (key) =>
            messageRef.current[parseInt(key)] || createRef<HTMLDivElement>(),
    );

    // 加载完成后仍延迟 1 秒卸载 Preloader，让 CSS 离场动画有时间播放。
    const [activeLetterName] = useActiveLetterState();
    const [preloaderReady] = usePreloaderReadyState();
    const debouncedPreloaderReady = useDebounce(preloaderReady, 1000);
    // 首次声音选择完成后也延迟卸载遮罩，避免按钮点击后画面瞬间跳变。
    const [config] = useSoundConfigState();
    const debouncedSoundConfig = useDebounce(config, 1000);
    const [currentScreen] = useScreensState();

    // 3D 网格只保存 name；完整正文在 lettersData 中按名称查找。
    const activeLetterData = lettersData.find(
        (letter) => letter.name === activeLetterName,
    );
    // 关闭弹窗时 activeLetterData 会立刻变成 undefined；保留 300ms 旧内容完成退场。
    const debouncedActiveLetterData = useDebounce(activeLetterData, 300);
    const letterData = !!activeLetterData
        ? activeLetterData
        : debouncedActiveLetterData;

    return (
        <main className="main">
            {/* 两层启动遮罩：先等待资源，再让用户决定是否开启声音。 */}
            {!debouncedPreloaderReady && <Preloader />}
            {!debouncedSoundConfig.picked && <SoundConfiguratorScreen />}

            <Header />

            {/* 只为配置了 message 的章节创建文案；visible 决定 CSS 显隐。 */}
            {Object.keys(data).map(
                (key) =>
                    data[parseInt(key)].message && (
                        <Message
                            key={key}
                            ref={messageRef.current[parseInt(key)]}
                            title={data[parseInt(key)].message?.title || ''}
                            text={data[parseInt(key)].message?.text || ''}
                            bgUrl={data[parseInt(key)].message?.bgUrl}
                            classPostfix={
                                data[parseInt(key)].message?.classPostfix || ''
                            }
                            visible={parseInt(key) === currentScreen}
                        />
                    ),
            )}

            {/* 第 13 章没有普通 Message，而是专用的结尾祝福。 */}
            <div
                className={classNames('last-message', {
                    'is-visible': currentScreen === 13,
                })}
            >
                <span>Happy</span>
                <span>NewYear!</span>
            </div>

            <ProgressRound />

            {/* opened 由当前是否命中客户贺卡决定；letterData 保证退场时仍有内容。 */}
            <LetterPopup
                opened={!!activeLetterData}
                from={letterData?.from}
                to={letterData?.to}
                logoUrl={letterData?.logoUrl}
                text={letterData?.text}
            />

            <Menu />

            <Sounds />
        </main>
    );
};

export default Dom;
